import React, { useEffect, useState } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';

export interface IpBlocklistRow {
  id: number;
  entryType?: 'IP' | 'EMAIL' | 'PHONE' | string;
  ipAddress: string;
  value?: string;
  valueDisplay?: string;
  reason?: string | null;
  enabled: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
}

type EntryType = 'IP' | 'EMAIL' | 'PHONE';

const IpBlocklist: React.FC = () => {
  const adminApi = useAdminApiService();
  const [rows, setRows] = useState<IpBlocklistRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [entryType, setEntryType] = useState<EntryType>('IP');
  const [newValue, setNewValue] = useState('');
  const [newReason, setNewReason] = useState('');
  const [saving, setSaving] = useState(false);

  const displayValue = (row: IpBlocklistRow) =>
    row.valueDisplay || row.value || row.ipAddress || '—';

  const loadEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getIpBlocklist();
      if (res.success && res.data) {
        setRows(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.error || 'Failed to load security blocklist');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load security blocklist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const handleAdd = async () => {
    const value = newValue.trim();
    if (!value) {
      setError(`Enter a ${entryType.toLowerCase()} value`);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.addIpToBlocklist(value, newReason.trim() || undefined, entryType);
      if (res.success) {
        setNewValue('');
        setNewReason('');
        setSuccess(`${entryType} ${value} added to blocklist`);
        await loadEntries();
      } else {
        setError(res.error || 'Failed to add entry');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row: IpBlocklistRow) => {
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.setIpBlocklistEnabled(row.id, !row.enabled);
      if (res.success) {
        setSuccess(`${displayValue(row)} ${!row.enabled ? 'blocked' : 'enabled (unblocked)'}`);
        await loadEntries();
      } else {
        setError(res.error || 'Failed to update entry');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update entry');
    }
  };

  const handleDelete = async (row: IpBlocklistRow) => {
    if (!window.confirm(`Permanently remove ${displayValue(row)} from the blocklist?`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.deleteIpFromBlocklist(row.id);
      if (res.success) {
        setSuccess(`${displayValue(row)} removed`);
        await loadEntries();
      } else {
        setError(res.error || 'Failed to delete entry');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete entry');
    }
  };

  const placeholder =
    entryType === 'IP'
      ? 'e.g. 203.0.113.42'
      : entryType === 'EMAIL'
        ? 'e.g. fraud@example.com'
        : 'e.g. +35799123456';

  return (
    <div className="admin-component">
      <div className="section-header">
        <h2 className="section-title">Security Blocklist</h2>
        <button className="refresh-btn" onClick={loadEntries} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, maxWidth: 720 }}>
        Block IP addresses, emails, or phone numbers on login/registration. Disable an entry to allow
        access again without deleting it. Auto-block rules (default OFF) are managed under Admin Settings → Security.
      </p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 16,
          alignItems: 'flex-end',
          padding: 16,
          background: '#f9fafb',
          borderRadius: 8,
          border: '1px solid #e5e7eb',
        }}
      >
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Type
          <select
            value={entryType}
            onChange={(e) => setEntryType(e.target.value as EntryType)}
            style={{ width: 120, padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          >
            <option value="IP">IP</option>
            <option value="EMAIL">Email</option>
            <option value="PHONE">Phone</option>
          </select>
        </label>
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Value
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder={placeholder}
            style={{ width: 240, padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          />
        </label>
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 200 }}>
          Reason (optional)
          <input
            type="text"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            placeholder="Suspicious activity from audit logs"
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          />
        </label>
        <button className="refresh-btn" onClick={handleAdd} disabled={saving || loading}>
          {saving ? 'Adding...' : `Block ${entryType}`}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {success && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            marginBottom: 12,
            background: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: 8,
            color: '#047857',
            fontSize: 13,
          }}
        >
          <span>{success}</span>
          <button
            type="button"
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16 }}
          >
            ×
          </button>
        </div>
      )}

      <div className="table-container" style={{ overflowX: 'auto' }}>
        <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 10 }}>Type</th>
              <th style={{ textAlign: 'left', padding: 10 }}>Value</th>
              <th style={{ textAlign: 'left', padding: 10 }}>Status</th>
              <th style={{ textAlign: 'left', padding: 10 }}>Reason</th>
              <th style={{ textAlign: 'left', padding: 10 }}>Updated</th>
              <th style={{ textAlign: 'left', padding: 10 }}>By</th>
              <th style={{ textAlign: 'left', padding: 10 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={7} style={{ padding: 16, color: '#6b7280' }}>
                  No blocked entries yet.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id}>
                <td style={{ padding: 10, fontSize: 12, fontWeight: 700 }}>
                  {row.entryType || 'IP'}
                </td>
                <td style={{ padding: 10, fontFamily: 'monospace', fontWeight: 600 }}>
                  {displayValue(row)}
                </td>
                <td style={{ padding: 10 }}>
                  <span
                    style={{
                      color: row.enabled ? '#b91c1c' : '#15803d',
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {row.enabled ? 'BLOCKED' : 'DISABLED'}
                  </span>
                </td>
                <td style={{ padding: 10, maxWidth: 320 }}>{row.reason || '—'}</td>
                <td style={{ padding: 10, fontSize: 12, color: '#6b7280' }}>
                  {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                </td>
                <td style={{ padding: 10, fontSize: 12 }}>{row.updatedBy || row.createdBy || '—'}</td>
                <td style={{ padding: 10 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="refresh-btn"
                      onClick={() => handleToggle(row)}
                      style={{ fontSize: 12, padding: '4px 10px' }}
                    >
                      {row.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="refresh-btn"
                      onClick={() => handleDelete(row)}
                      style={{ fontSize: 12, padding: '4px 10px', color: '#b91c1c' }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IpBlocklist;
